import React, { useMemo } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { InspecaoSnapshotResponse } from "../../../../services/inspecaoService";

const METRIC_GROUPS = [
  "Pizzas produzidas",
  "Tempo médio de produção",
  "Aguardando entrega",
  "Em entrega (rota)",
  "Falta montar",
] as const;

const TD_SELECTED_DAY =
  "border-l border-emerald-400/45 bg-emerald-800/65 text-white";
const TD_SELECTED_DAY_TOTAL =
  "border-l border-emerald-300/50 bg-emerald-950/85 text-white";
const TD_PREVIOUS_DAY =
  "border-l border-amber-300/35 bg-amber-700/40 text-white";
const TD_PREVIOUS_DAY_TOTAL =
  "border-l border-amber-300/40 bg-amber-900/55 text-white";

function snapshotRowKey(s: InspecaoSnapshotResponse): string {
  return `${s.horario}\0${s.isTotal ? "1" : "0"}`;
}

function renderPreviousTimeCell(
  isLoading: boolean,
  value: string | null | undefined,
  loadingSlot: React.ReactNode
): React.ReactNode {
  if (isLoading) return loadingSlot;
  if (value != null && value !== "") return value;
  return <span className="text-white/50">—</span>;
}

function renderPreviousCountCell(
  isLoading: boolean,
  value: number | null | undefined,
  loadingSlot: React.ReactNode
): React.ReactNode {
  if (isLoading) return loadingSlot;
  if (value != null) return value;
  return <span className="text-white/50">—</span>;
}

interface RankingInspecaoSnapshotsTvTableProps {
  snapshots: InspecaoSnapshotResponse[] | undefined;
  snapshotsPreviousPeriod: InspecaoSnapshotResponse[] | undefined;
  isLoading: boolean;
  isLoadingPreviousPeriod: boolean;
  lojaNomeResumo: string;
  showLojaColumn: boolean;
  comparisonPeriodLabel: string;
  comparisonDateLabel: string;
  selectedDateDayMonth: string;
  comparisonDateDayMonth: string;
}

type RowWithPrevious = InspecaoSnapshotResponse & {
  lojaNome: string;
  pizzasDeltaVsPreviousPeriod: number | null;
  pizzasPreviousPeriodSameMarco: number | null;
  previousPeriod: InspecaoSnapshotResponse | null;
};

export const RankingInspecaoSnapshotsTvTable: React.FC<
  RankingInspecaoSnapshotsTvTableProps
> = ({
  snapshots,
  snapshotsPreviousPeriod,
  isLoading,
  isLoadingPreviousPeriod,
  lojaNomeResumo,
  showLojaColumn,
  comparisonPeriodLabel,
  comparisonDateLabel,
  selectedDateDayMonth,
  comparisonDateDayMonth,
}) => {
  const previousByKey = useMemo(() => {
    const m = new Map<string, InspecaoSnapshotResponse>();
    for (const p of snapshotsPreviousPeriod ?? []) {
      m.set(snapshotRowKey(p), p);
    }
    return m;
  }, [snapshotsPreviousPeriod]);

  const rows = useMemo((): RowWithPrevious[] => {
    if (!snapshots?.length) return [];
    return snapshots.map((s) => {
      const key = snapshotRowKey(s);
      const previousPeriod = previousByKey.get(key) ?? null;
      const prevPeriodQty = previousPeriod?.quantidadeProducoes;
      const deltaVsWeek =
        prevPeriodQty != null && s.quantidadeProducoes != null
          ? s.quantidadeProducoes - prevPeriodQty
          : null;

      return {
        ...s,
        lojaNome: lojaNomeResumo,
        pizzasDeltaVsPreviousPeriod: deltaVsWeek,
        pizzasPreviousPeriodSameMarco: prevPeriodQty ?? null,
        previousPeriod,
      };
    });
  }, [snapshots, lojaNomeResumo, previousByKey]);

  const bodyColCount = 1 + (showLojaColumn ? 1 : 0) + METRIC_GROUPS.length * 2;

  if (isLoading) {
    return (
      <div className="flex max-h-full min-h-0 w-full items-center justify-center rounded-lg border border-white/20 bg-white/10 px-4 py-6 backdrop-blur-sm sm:rounded-xl sm:py-8">
        <p className="text-center text-base text-white/85 sm:text-lg">
          Carregando intervalos...
        </p>
      </div>
    );
  }

  const thBase =
    "whitespace-nowrap px-2 py-2 text-left text-[10px] font-semibold uppercase leading-tight tracking-wide text-white/90 sm:px-2.5 sm:py-2.5 sm:text-xs md:text-sm lg:px-3";
  const thSub =
    "whitespace-nowrap px-2 py-1.5 text-left text-[9px] font-bold leading-tight normal-case tracking-normal text-white/80 sm:px-2.5 sm:py-2 sm:text-[10px] md:text-xs lg:px-3";
  const tdBase =
    "leading-tight whitespace-nowrap px-2 py-2 text-sm tabular-nums sm:px-2.5 sm:py-2.5 sm:text-base md:px-3 md:py-2.5 md:text-base lg:text-lg";

  const renderPreviousPlaceholder = () =>
    isLoadingPreviousPeriod ? (
      <span className="text-white/40">…</span>
    ) : (
      <span className="text-white/50">—</span>
    );

  return (
    <div className="flex max-h-full min-h-0 w-full shrink grow-0 flex-col">
      <div className="flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm sm:rounded-xl">
        <div className="max-h-full min-h-0 w-full overflow-x-auto overflow-y-auto">
          <table className="w-full min-w-[780px] divide-y divide-white/10 lg:min-w-[880px]">
            <thead className="sticky top-0 z-10 bg-red-900/80 backdrop-blur-sm">
              <tr>
                <th
                  rowSpan={2}
                  scope="col"
                  className={`${thBase} align-bottom`}
                >
                  Intervalo
                </th>
                {showLojaColumn ? (
                  <th
                    rowSpan={2}
                    scope="col"
                    className={`${thBase} align-bottom`}
                  >
                    Loja
                  </th>
                ) : null}
                {METRIC_GROUPS.map((label) => (
                  <th
                    key={label}
                    scope="colgroup"
                    colSpan={2}
                    title={`${label}: período selecionado e ${comparisonPeriodLabel.toLowerCase()} (${comparisonDateLabel})`}
                    className={`${thBase} border-l border-white/10 text-center`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
              <tr>
                {METRIC_GROUPS.map((label) => (
                  <React.Fragment key={`${label}-sub`}>
                    <th
                      scope="col"
                      className={`${thSub} border-l border-white/10`}
                    >
                      {selectedDateDayMonth}
                    </th>
                    <th
                      scope="col"
                      className={thSub}
                      title={`${comparisonPeriodLabel}: ${comparisonDateLabel}`}
                    >
                      {comparisonPeriodLabel} {comparisonDateDayMonth}
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={bodyColCount}
                    className="px-4 py-8 text-center text-sm text-white/75 sm:py-10 sm:text-base md:text-lg"
                  >
                    Nenhum intervalo para exibir nesta data ou loja.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={`${row.horario}-${row.id}`}
                    className={
                      row.isTotal
                        ? "font-semibold text-white"
                        : "text-white/95 hover:bg-white/[0.04]"
                    }
                  >
                    <td
                      className={`${tdBase} bg-black/20 font-semibold text-white ${row.isTotal ? "bg-black/35" : ""}`}
                    >
                      {row.horario}
                    </td>
                    {showLojaColumn ? (
                      <td
                        className={`${tdBase} bg-black/20 font-semibold text-white/95 ${row.isTotal ? "bg-black/35" : ""}`}
                      >
                        {row.lojaNome}
                      </td>
                    ) : null}
                    <td
                      className={`${tdBase} tabular-nums ${row.isTotal ? TD_SELECTED_DAY_TOTAL : TD_SELECTED_DAY}`}
                    >
                      {row.quantidadeProducoes != null ? (
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <span className="inline-block min-w-[4ch] text-right font-semibold">
                            {row.quantidadeProducoes}
                          </span>
                          {!isLoadingPreviousPeriod &&
                          row.pizzasDeltaVsPreviousPeriod != null &&
                          row.pizzasDeltaVsPreviousPeriod !== 0 ? (
                            <span
                              title={
                                row.pizzasPreviousPeriodSameMarco != null
                                  ? `Mesmo marco em ${comparisonDateLabel}: ${row.pizzasPreviousPeriodSameMarco}`
                                  : undefined
                              }
                              className={[
                                "inline-flex min-w-[3rem] items-center justify-center gap-0.5 rounded px-2 py-0.5 text-[11px] font-bold tabular-nums sm:min-w-[3.25rem] sm:text-xs md:text-sm",
                                row.pizzasDeltaVsPreviousPeriod < 0
                                  ? "bg-red-600/50 text-white"
                                  : "bg-emerald-500/45 text-emerald-50",
                              ].join(" ")}
                            >
                              {row.pizzasDeltaVsPreviousPeriod < 0 ? (
                                <ArrowDown className="shrink-0" size={14} />
                              ) : row.pizzasDeltaVsPreviousPeriod > 0 ? (
                                <ArrowUp className="shrink-0" size={14} />
                              ) : null}
                              <span className="min-w-[2ch] text-center">
                                {Math.abs(row.pizzasDeltaVsPreviousPeriod)}
                              </span>
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-white/50">—</span>
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_PREVIOUS_DAY_TOTAL : TD_PREVIOUS_DAY}`}
                    >
                      {renderPreviousCountCell(
                        isLoadingPreviousPeriod,
                        row.previousPeriod?.quantidadeProducoes,
                        renderPreviousPlaceholder()
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_SELECTED_DAY_TOTAL : TD_SELECTED_DAY}`}
                    >
                      {row.tempoMedioProducao ?? (
                        <span className="text-white/55">—</span>
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_PREVIOUS_DAY_TOTAL : TD_PREVIOUS_DAY}`}
                    >
                      {renderPreviousTimeCell(
                        isLoadingPreviousPeriod,
                        row.previousPeriod?.tempoMedioProducao,
                        renderPreviousPlaceholder()
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_SELECTED_DAY_TOTAL : TD_SELECTED_DAY}`}
                    >
                      {row.tempoMedioAguardandoEntrega ?? (
                        <span className="text-white/55">—</span>
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_PREVIOUS_DAY_TOTAL : TD_PREVIOUS_DAY}`}
                    >
                      {renderPreviousTimeCell(
                        isLoadingPreviousPeriod,
                        row.previousPeriod?.tempoMedioAguardandoEntrega,
                        renderPreviousPlaceholder()
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_SELECTED_DAY_TOTAL : TD_SELECTED_DAY}`}
                    >
                      {row.tempoMedioEmEntrega ?? (
                        <span className="text-white/55">—</span>
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_PREVIOUS_DAY_TOTAL : TD_PREVIOUS_DAY}`}
                    >
                      {renderPreviousTimeCell(
                        isLoadingPreviousPeriod,
                        row.previousPeriod?.tempoMedioEmEntrega,
                        renderPreviousPlaceholder()
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_SELECTED_DAY_TOTAL : TD_SELECTED_DAY}`}
                    >
                      {row.pizzasAProduzir != null ? (
                        row.pizzasAProduzir
                      ) : (
                        <span className="text-white/55">—</span>
                      )}
                    </td>
                    <td
                      className={`${tdBase} font-medium tabular-nums ${row.isTotal ? TD_PREVIOUS_DAY_TOTAL : TD_PREVIOUS_DAY}`}
                    >
                      {renderPreviousCountCell(
                        isLoadingPreviousPeriod,
                        row.previousPeriod?.pizzasAProduzir ?? null,
                        renderPreviousPlaceholder()
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
